/**
 * Local Intelligence Service
 *
 * Provides basic task optimization without API calls.
 * Used for routine optimizations when API quota is exceeded
 * or crisis-level accuracy is not required.
 *
 * NOTE: This is a stub implementation. Full local intelligence
 * capabilities are planned for future development.
 */

export class LocalIntelligence {
  constructor() {
    this.name = 'LocalIntelligence';
  }

  /**
   * Generate basic suggestions using local algorithms
   * @param {Array} tasks - Current tasks
   * @param {Object} userContext - User context
   * @returns {Object} - Basic suggestions
   */
  async generateSuggestions(tasks, userContext) {
    // Stub implementation - returns basic batching suggestions
    const overdueTasks = tasks.filter(task => {
      const dueDate = new Date(task.deadline || task.scheduled_date);
      return dueDate < new Date() && !task.completed;
    });

    const upcomingTasks = tasks.filter(task => {
      const dueDate = new Date(task.deadline || task.scheduled_date);
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return dueDate >= new Date() && dueDate <= tomorrow && !task.completed;
    });

    return {
      explanation: {
        summary: "Basic task organization suggestions",
        reasoning: "Local optimization based on deadlines and task patterns",
        priorities: ["Address overdue tasks", "Prepare for tomorrow's deadlines", "Group similar tasks"]
      },
      actions: {
        taskUpdates: this.generateBasicReschedules(overdueTasks),
        batchCreation: this.generateBasicBatches(tasks),
        timelineAdjustments: {}
      },
      metadata: {
        source: 'local',
        confidence: 0.7,
        reversible: true,
        time_saved_hours: Math.min(overdueTasks.length * 0.5, 3)
      }
    };
  }

  /**
   * Generate basic reschedule suggestions for overdue tasks
   */
  generateBasicReschedules(overdueTasks) {
    return overdueTasks.slice(0, 5).map(task => ({
      taskId: task.id,
      action: 'reschedule',
      newDate: this.getNextAvailableSlot(),
      reason: `Reschedule overdue task: ${task.title}`
    }));
  }

  /**
   * Generate basic batching suggestions
   */
  generateBasicBatches(tasks) {
    // Group tasks by type/subject for batching
    const tasksByType = tasks.reduce((groups, task) => {
      const type = task.subject || task.category || 'general';
      if (!groups[type]) groups[type] = [];
      groups[type].push(task);
      return groups;
    }, {});

    const batches = [];
    Object.entries(tasksByType).forEach(([type, typeTasks]) => {
      if (typeTasks.length >= 2) {
        batches.push({
          title: `${type} batch`,
          tasks: typeTasks.slice(0, 3).map(t => t.id),
          reason: `Group ${type} tasks for focused work session`
        });
      }
    });

    return batches.slice(0, 2); // Limit to 2 batches
  }

  /**
   * Get next available time slot (stub implementation)
   */
  getNextAvailableSlot() {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0); // 9 AM tomorrow
    return tomorrow.toISOString();
  }

  /**
   * Generate conservative suggestions when API quota is exceeded
   * @param {Array} tasks - Current tasks
   * @param {Object} userContext - User context
   * @returns {Object} - Conservative suggestions
   */
  async generateConservativeSuggestions(tasks, userContext) {
    const overdueTasks = tasks.filter(task => {
      const dueDate = new Date(task.deadline || task.scheduled_date);
      return dueDate < new Date() && !task.completed;
    });

    // Sort overdue tasks by priority
    const prioritizedTasks = overdueTasks.sort((a, b) => {
      if (a.priority === 'critical' && b.priority !== 'critical') return -1;
      if (b.priority === 'critical' && a.priority !== 'critical') return 1;
      return 0;
    });

    return {
      explanation: {
        summary: "Conservative recovery suggestions (quota limit reached)",
        reasoning: "Focus on the most critical overdue tasks first",
        priorities: [
          `Complete ${Math.min(3, prioritizedTasks.length)} critical tasks today`,
          "Reschedule non-critical tasks to next week",
          "Consider asking for extensions where possible"
        ]
      },
      actions: {
        taskUpdates: prioritizedTasks.slice(0, 5).map(task => ({
          taskId: task.id,
          action: 'focus',
          priority: 'critical',
          reason: `Focus on completing: ${task.title}`
        })),
        batchCreation: [],
        timelineAdjustments: {
          suggestedAction: 'postpone_non_critical',
          daysToShift: 7
        }
      },
      metadata: {
        source: 'local_conservative',
        confidence: 0.6,
        reversible: true,
        isQuotaFallback: true
      }
    };
  }

  /**
   * Assess whether local intelligence is sufficient for this request
   */
  canHandle(userContext, tasks) {
    const overdueTasks = tasks.filter(task => {
      const dueDate = new Date(task.deadline || task.scheduled_date);
      return dueDate < new Date() && !task.completed;
    });

    // Only handle non-crisis situations
    return overdueTasks.length < 5 && userContext.riskLevel !== 'crisis';
  }
}

export default LocalIntelligence;